const _scope = (value, label) => ({
  extends: 'BroadcastAudienceSegment',
  value,
  label
})

export const announcements = {
  extends: 'AppPage',
  // SIZE-1 (tickets/sonnet.md, 2026-08-14): the route is `/announcements`
  // but every user-facing string here said "Broadcast" — the page's own H1,
  // the tab title, and the sidebar's aria-label all disagreed with the URL.
  // Fixed to "Announcements" throughout the user-facing copy below.
  // Internal identifiers (BroadcastNavigation, BroadcastTagFilter,
  // canPublishBroadcast, openBroadcastComposer, …) are untouched — those are
  // the feature's internal name, not copy, and renaming them is a much
  // larger, unrelated change this ticket didn't ask for.
  metadata: (el) => ({ title: el.call('pageTitle', 'workspace.announcements.page.title') }),
  state: { announcementsLoaded: false, announcementsError: null },

  // `loadPrivateData` normally warms this collection after sign-in. The
  // declarative page fetch makes a direct /announcements visit dependable too
  // and keeps the screen correct after a workspace switch.
  fetch: (el) => [
    ...el.context.functions.appPageBoilerplateFetch,
    {
      // The fetch SDK adapter maps declarative `select` to the SDK entity's
      // `list` operation. `method: 'list'` itself is not an adapter method.
      method: 'select',
      from: 'workspaceProject.announcements',
      order: 'date',
      as: '~/announcements',
      cache: '15s',
      onFetchStart: (el, s) => {
        s.update({ announcementsLoaded: false, announcementsError: null }, { preventFetch: true })
      },
      transform: (rows, el, s) => {
        // Transform also runs for a cache hit, which skips onFetchComplete.
        s.update({ announcementsLoaded: true, announcementsError: null }, { preventFetch: true })
        const announcements = []
        for (const row of rows || []) announcements.push(el.call('broadcastFromWire', row))
        return { announcements }
      },
      onFetchError: (error, el, s) => {
        s.update(
          { announcementsLoaded: false, announcementsError: 'workspace.announcements.loadFailed' },
          { preventFetch: true }
        )
      }
    }
  ],

  AppSidebar: {
    tag: 'nav',
    'aria-label': (el) => el.call('polyglot', 'workspace.announcements.navAria'),
    // On the page-sidebar rule (shared functions/pageSidebar.js): opposite a
    // rail navbar, hideable per page. The first card opens with its caption, so
    // the hide button's corner is free.
    pageSidebar: true,
    '@tabletS': { width: 'F1', minWidth: 'F1' },

    BroadcastNavigation: {
      flow: 'y',
      gap: 'A',

      Audience: {
        flow: 'y',
        gap: 'Z',
        CaptionTitle: {
          fontSize: 'Y2',
          Text: { text: '{{ workspace.announcements.audience | polyglot }}' }
        },
        Segments: {
          flow: 'x',
          gap: 'Y',
          'aria-label': (el) => el.call('polyglot', 'workspace.announcements.audienceAria'),
          Internal: _scope('internal', 'workspace.announcements.scope.team'),
          External: _scope('external', 'workspace.announcements.scope.public'),
          All: _scope('all', 'workspace.announcements.scope.all')
        }
      },

      Topics: {
        flow: 'y',
        gap: 'Z',
        CaptionTitle: {
          fontSize: 'Y2',
          Text: { text: '{{ workspace.announcements.topics | polyglot }}' }
        },
        List: {
          flow: 'y',
          gap: 'V',
          childExtends: 'BroadcastTagFilter',
          childrenAs: 'state',
          children: (el, s) => el.call('broadcastTags', s.root)
        }
      }
    }
  },

  AppMain: {
    Body: {
      flow: 'y',
      gap: 'C',
      padding: 'C',
      overflow: 'auto',
      minWidth: '0',

      // The page head on the ground (the page-head pattern the built-in
      // /bookmarks head first set; __tests__/pageHeadsFlatLaw.test.js pins
      // it): label, title and lead, the main action on the right. It was a full colour slab
      // (`purple+30` ground, `black` ink, `D` padding, the sheet radius),
      // against the flat/minimal law (design-d2, 2026-10-01).
      Hero: {
        flow: 'x',
        align: 'flex-end space-between',
        gap: 'B',
        flexWrap: 'wrap',
        '@mobileL': { flow: 'y', align: 'stretch flex-start' },
        Copy: {
          flow: 'y',
          gap: 'X',
          minWidth: '0',
          maxWidth: 'H',
          Eyebrow: {
            tag: 'span',
            color: 'inactive',
            fontSize: 'Y2',
            fontWeight: '700',
            text: '{{ workspace.announcements.eyebrow | polyglot }}'
          },
          Title: {
            // One title per host (design-direction.md §3): under a pane title
            // bar the poster keeps its eyebrow, copy and action, and drops
            // the line that repeats the bar.
            hide: (el, s) => el.call('paneTitleBarShown', s),
            tag: 'h1',
            margin: '0',
            color: 'title',
            fontSize: 'D',
            fontWeight: '700',
            lineHeight: '1.05',
            text: '{{ workspace.announcements.title | polyglot }}'
          },
          Description: {
            tag: 'p',
            margin: '0',
            color: 'caption',
            fontSize: 'Z2',
            text: '{{ workspace.announcements.description | polyglot }}'
          }
        },
        Publish: {
          tag: 'button',
          type: 'button',
          hide: (el, s) => !el.call('canPublishBroadcast', s.root),
          minHeight: 'controlH',
          padding: '0 A',
          round: 'radiusPill',
          borderWidth: '0',
          cursor: 'pointer',
          theme: 'primary',
          fontWeight: '700',
          text: '{{ workspace.announcements.writeRelease | polyglot }}',
          onClick: (event, el) => {
            event.preventDefault()
            el.call('openBroadcastComposer')
          }
        }
      },

      Toolbar: {
        flow: 'x',
        align: 'center space-between',
        gap: 'A',
        flexWrap: 'wrap',
        Search: {
          tag: 'input',
          type: 'search',
          name: 'news-search',
          ariaLabel: (el) => el.call('polyglot', 'workspace.announcements.searchAria'),
          autocomplete: 'off',
          minHeight: 'controlHCompact',
          minWidth: '0',
          width: 'G',
          maxWidth: '100%',
          padding: '0 A',
          round: 'radiusPill',
          border: 'none',
          background: 'overlay',
          color: 'title',
          fontSize: 'Z1',
          placeholder: (el) => el.call('polyglot', 'workspace.announcements.search'),
          value: (el, s) => s.root?._broadcastQuery || '',
          onInput: (event, el) => el.call('setBroadcastQuery', event.target.value)
        },
        Summary: {
          hide: (el, s) => !s.announcementsLoaded || !!s.announcementsError,
          tag: 'span',
          fontFamily: 'Code',
          fontSize: 'Y2',
          color: 'caption',
          text: (el, s) =>
            el
              .call('polyglot', 'workspace.announcements.updateCount')
              .replace('{count}', el.call('broadcastItemsForView', s.root).length)
        }
      },

      Error: {
        hide: (el, s) => !s.root?._broadcastSaveError,
        tag: 'p',
        margin: '0',
        padding: 'Z A',
        round: 'radiusControl',
        background: 'red.15',
        color: 'dangerInk',
        fontSize: 'Z1',
        role: 'status',
        ariaLive: 'polite',
        text: (el, s) => s.root?._broadcastSaveError || ''
      },

      Loading: {
        hide: (el, s) => !!s.announcementsLoaded || !!s.announcementsError,
        role: 'status',
        ariaLabel: (el) => el.call('polyglot', 'common.state.loading'),
        flow: 'y',
        gap: 'A',
        Skeleton_1: { height: 'D', 'aria-hidden': true },
        Skeleton_2: { height: 'D', 'aria-hidden': true },
        Skeleton_3: { height: 'D', 'aria-hidden': true }
      },

      ErrorState: {
        hide: (el, s) => !s.announcementsError,
        kind: 'serverError',
        heading: 'workspace.announcements.loadFailed',
        hintText: 'workspace.announcements.tryAgain',
        IconWrap: { Icon: { icon: 'infoOval' } },
        Button: {
          theme: 'primary',
          text: '{{ common.action.try_again | polyglot }}',
          onClick: (event, el) => {
            event.preventDefault()
            const page = el.lookup((ancestor) => !!ancestor.__ref?.refetch)
            if (!page) return
            page.state.update(
              { announcementsLoaded: false, announcementsError: null },
              { preventFetch: true }
            )
            page.__ref.refetch()
          }
        }
      },

      Feed: {
        hide: (el, s) => !s.announcementsLoaded || !!s.announcementsError,
        flow: 'y',
        gap: 'A',
        maxWidth: 'I',
        childExtends: 'BroadcastStoryCard',
        childrenAs: 'state',
        children: (el, s) => el.call('broadcastVisibleItems', s.root)
      },

      More: {
        hide: (el, s) =>
          !s.announcementsLoaded ||
          !!s.announcementsError ||
          el.call('broadcastItemsForView', s.root).length <= (s.root?._broadcastVisibleCount || 50),
        tag: 'button',
        type: 'button',
        alignSelf: 'flex-start',
        minHeight: 'controlHCompact',
        padding: '0 A',
        round: 'radiusPill',
        border: 'none',
        background: 'overlay',
        color: 'title',
        fontSize: 'Z1',
        fontWeight: '700',
        cursor: 'pointer',
        text: '{{ workspace.announcements.showMore | polyglot }}',
        onClick: (event, el) => {
          event.preventDefault()
          el.call('showMoreBroadcastItems')
        }
      },

      Empty: {
        hide: (el, s) =>
          !s.announcementsLoaded ||
          !!s.announcementsError ||
          el.call('broadcastItemsForView', s.root).length > 0,
        flow: 'y',
        align: 'center center',
        gap: 'A',
        padding: 'E C',
        round: 'radiusSheet',
        background: 'overlay',
        textAlign: 'center',
        Icon: { name: 'bell', boxSize: 'C', color: 'caption' },
        Title: {
          tag: 'h2',
          margin: '0',
          fontSize: 'B',
          fontWeight: '700',
          color: 'title',
          text: (el, s) =>
            el.call(
              'polyglot',
              el.call('broadcastHasActiveFilters', s.root)
                ? 'workspace.announcements.empty.filteredTitle'
                : s.root?._broadcastScope === 'external'
                  ? 'workspace.announcements.empty.publicTitle'
                  : s.root?._broadcastScope === 'all'
                    ? 'workspace.announcements.empty.allTitle'
                    : 'workspace.announcements.empty.teamTitle'
            )
        },
        Copy: {
          tag: 'p',
          margin: '0',
          maxWidth: 'G',
          fontSize: 'Z1',
          color: 'caption',
          text: (el, s) =>
            el.call(
              'polyglot',
              el.call('broadcastHasActiveFilters', s.root)
                ? 'workspace.announcements.empty.filteredCopy'
                : el.call('canPublishBroadcast', s.root)
                  ? 'workspace.announcements.empty.publisherCopy'
                  : 'workspace.announcements.empty.readerCopy'
            )
        },
        Button: {
          hide: (el, s) => !el.call('broadcastHasActiveFilters', s.root),
          theme: 'primary',
          text: '{{ workspace.announcements.clearFilters | polyglot }}',
          onClick: (event, el) => {
            event.preventDefault()
            el.call('clearBroadcastFilters')
          }
        },
        Publish: {
          tag: 'button',
          type: 'button',
          hide: (el, s) =>
            el.call('broadcastHasActiveFilters', s.root) || !el.call('canPublishBroadcast', s.root),
          minHeight: 'controlHCompact',
          padding: '0 A',
          round: 'radiusPill',
          border: 'none',
          background: 'primary',
          color: 'primary-content',
          fontSize: 'Z1',
          fontWeight: '700',
          cursor: 'pointer',
          text: '{{ workspace.announcements.writeRelease | polyglot }}',
          onClick: (event, el) => {
            event.preventDefault()
            el.call('openBroadcastComposer')
          }
        }
      }
    }
  }
}
